import { Box, Grid2, TextField } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import useNavigationGuard from "../../../hooks/useNavigationGuard";
import {
  checkCanUpdate,
  notifyError,
  notifySuccess,
} from "../../../utilities/helper";
import {
  addLabelGroupService,
  fetchCountriesService,
  fetchLabelGroupByIdService,
  updateLabelGroupService,
} from "../../../services/labelGroupManagementService";
import { MenuContext } from "../../../App";
import { CreateHandleUnsaveNavigation } from "../../../utilities/handleUnsaveNavigation";
import MultiSelectSearch from "../../../components/MultiSelectSearch";

const AddUpdateLabelGroupManagement = () => {
  const id = useLocation()?.state?.id;

  const [load, setLoad] = useState(false);
  const [countries, setCountries] = useState([]);
  const [canUpdateLabelGroup, setCanUpdateLabelGroup] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const access_token = localStorage?.getItem("access_token");
  const role = localStorage.getItem("role");

  const { systemActions } = useContext(MenuContext);
  const handleUnsaveNavigation = CreateHandleUnsaveNavigation();

  const navigate = useNavigate();

  useEffect(() => {
    if (access_token) {
      fetchCountries();
    }
  }, [access_token]);

  useEffect(() => {
    if (id && countries && countries?.length > 0) {
      getLabelGroupById(id);
    }
  }, [id, countries]);

  useEffect(() => {
    if (systemActions?.length > 0 && role !== "Admin" && id) {
      let canUpdate = false;
      const updateFound = systemActions?.find(
        (item) => item?.name === "CanEditLabelGroups"
      );
      if (updateFound) {
        canUpdate = true;
      }
      setCanUpdateLabelGroup(canUpdate);
    } else {
      if (!id || role === "Admin") {
        setCanUpdateLabelGroup(true);
      }
    }
  }, [systemActions, id]);

  useEffect(() => {
    if (canUpdateLabelGroup) {
      setIsReadOnly(false);
    } else {
      setIsReadOnly(true);
    }
  }, [canUpdateLabelGroup]);

  const createLabelGrpSchema = yup.object().shape({
    name: yup
      .string()
      .required("Name is required")
      .matches(
        /^[A-Za-z][A-Za-z0-9\s!@#$%^&*()_+=-]*$/,
        "Name must not start with a number or special character"
      )
      .test("has-letter", "Name must contain at least one alphabet", (value) =>
        /[A-Za-z]/.test(value || "")
      ),
    countries: yup.array().min(1, "At least one country is required"),
    notes: yup.string().required("Notes is required"),
  });

  const {
    register,
    control,
    setValue,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(createLabelGrpSchema),
    defaultValues: {
      name: "",
      countries: [],
      notes: "",
    },
  });

  useNavigationGuard(isDirty);

  const getLabelGroupById = async (id) => {
    setLoad(true);
    try {
      const res = await fetchLabelGroupByIdService(access_token, id);
      if (res?.status == 200) {
        const resp = res?.data?.result;
        setLoad(false);
        reset({
          countries: resp?.selectedCountryIDS,
          name: resp?.name,
          notes: resp?.notes,
        });
      }
    } catch (err) {
      setLoad(false);
      console.log(err.message);
      notifyError(err?.message);
      if (err?.status == 401) {
        navigate("/unauthorize");
      }
    }
  };

  const addUpdateLabelGroup = async (data) => {
    setLoad(true);
    const newData = {
      name: data?.name,
      notes: data?.notes,
      selectedCountryIDS: data?.countries,
    };

    try {
      if (!id) {
        const res = await addLabelGroupService(access_token, newData);
        if (res?.data?.status == 409) {
          setLoad(false);
          notifyError("Label Group already exists !");
          return;
        } else if (res?.status == 200) {
          setLoad(false);
          notifySuccess("Label Group is created successfully !");
          reset();
          navigate("/label-group-management");
        }
      } else {
        const res = await updateLabelGroupService(access_token, newData, id);
        if (res?.status == 200) {
          setLoad(false);
          notifySuccess("Label Group is Updated successfully !");
          reset();
          navigate("/label-group-management");
        }
      }
    } catch (err) {
      setLoad(false);
      console.log(err.message);
      notifyError(err.message);
      if (err.status == 401) {
        navigate("/unauthorize");
      }
    }
  };

  const fetchCountries = async () => {
    try {
      setLoad(true);
      const res = await fetchCountriesService(access_token);
      if (res?.status == 200) {
        setLoad(false);
        setCountries(res?.data?.result);
      }
    } catch (err) {
      setLoad(false);
      console.log(err?.message);
    }
  };

  return (
    <>
      {load ? (
        <Loader />
      ) : (
        <div className="content-body">
          <p className="bread-crumbs-link-container">
            <button
              className="bread-crumbs-btn"
              onClick={(e) => handleUnsaveNavigation(e, isDirty, "", navigate)}
            >
              Home
            </button>{" "}
            |{" "}
            <button
              className="bread-crumbs-btn"
              onClick={(e) =>
                handleUnsaveNavigation(
                  e,
                  isDirty,
                  "label-group-management",
                  navigate
                )
              }
            >
              Label Group Management
            </button>{" "}
            |{" "}
            <button
              className="bread-crumbs-btn"
              to="/addUpdate-label-group-management"
            >
              {id
                ? "Edit Label Group Management"
                : "Add Label Group Management"}
            </button>
            -{" "}
            <b style={{ color: "gray" }}>
              {localStorage?.getItem("studyName")}
            </b>
          </p>
          <Grid2
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%", margin: "0 auto" }}
          >
            <Grid2 item md={12}>
              <p className="page-heading">Label Group Details</p>
            </Grid2>
          </Grid2>

          <Box
            component="form"
            autoComplete="off"
            sx={{ height: "auto", width: "100%" }}
            onSubmit={handleSubmit(addUpdateLabelGroup)}
          >
            <Grid2
              container
              alignItems="center"
              justifyContent={"space-between"}
              sx={{ width: "100%", margin: "0 auto" }}
              gap={1}
            >
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Name
                    <span>*</span>
                  </p>
                  <TextField
                    size="small"
                    disabled={isReadOnly}
                    fullWidth
                    type={"text"}
                    {...register("name")}
                  />
                </div>
                {errors.name && (
                  <div className="error-container">{errors.name.message}</div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Countries
                    <span>*</span>
                  </p>
                  {/* <Controller
                    name="countries"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="countries-label"
                        multiple
                        fullWidth
                        disabled={isReadOnly}
                        size="small"
                        renderValue={(selected) =>
                          countries
                            .filter((country) => selected.includes(country.id))
                            .map((country) => country.name)
                            .join(", ")
                        }
                        onChange={(e) =>
                          setValue("countries", e.target.value, {
                            shouldValidate: true,
                            shouldDirty: true,
                          })
                        }
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {countries &&
                          countries?.map((country, index) => (
                            <MenuItem key={index} value={country?.id}>
                              <Checkbox
                                checked={field.value.includes(country?.id)}
                              />
                              <ListItemText primary={country?.name} />
                            </MenuItem>
                          ))}
                      </Select>
                    )}
                  /> */}
                  <MultiSelectSearch
                    control={control}
                    data={countries}
                    name={"countries"}
                    setValue={setValue}
                    optionKey="id"
                    optionLabel="name"
                    isReadOnly={isReadOnly}
                  />
                </div>
                {errors.countries && (
                  <div className="error-container">
                    {errors.countries.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Notes
                    <span>*</span>
                  </p>
                  <TextField
                    fullWidth
                    disabled={isReadOnly}
                    size="small"
                    type={"text"}
                    {...register("notes")}
                  />
                </div>
                {errors.notes && (
                  <div className="error-container">{errors.notes.message}</div>
                )}
              </Grid2>
            </Grid2>

            <Grid2
              alignItems={"end"}
              justifyContent="end"
              container
              gap={3}
              sx={{ marginTop: "2%" }}
            >
              <Grid2 item md={6}>
                <button
                  type="button"
                  className="create-btn-outlined"
                  onClick={(e) => {
                    e.preventDefault();
                    handleUnsaveNavigation(
                      e,
                      isDirty,
                      "label-group-management",
                      navigate
                    );
                  }}
                >
                  Cancel
                </button>
              </Grid2>
              <Grid2 item md={6}>
                {checkCanUpdate(id, isReadOnly, role) && (
                  <button
                    type="submit"
                    disabled={isReadOnly}
                    className={isReadOnly ? "disabled-btn" : "create-btn"}
                  >
                    {id ? "Update" : "Save"}
                  </button>
                )}
              </Grid2>
            </Grid2>
          </Box>
        </div>
      )}
    </>
  );
};

export default AddUpdateLabelGroupManagement;
